import { useId } from 'react';
import { createSvgIcon, SvgIcon, SvgIconProps } from '@mui/material';

// Petals drawn in a 100-unit box (lotus spans 100 x 95.3), scaled into the 24px viewBox per variant.
const LEFT_PETAL = 'M0 36.3C22 38 42 52 50 95.3C26 90 4 76 0 36.3Z';
const RIGHT_PETAL = 'M100 36.3C78 38 58 52 50 95.3C74 90 96 76 100 36.3Z';
const CENTER_PETAL = 'M50 0C57 18 62 36 61 52C60 68 55 82 50 95.3C45 82 40 68 39 52C38 36 43 18 50 0Z';

// Brand artwork colours, matched to the existing Gargi (CasaAI) icon's indigo/lilac/white.
// Exported so surfaces that sit next to the badge (toggle button, processing chip) use the
// exact same indigo instead of the lighter theme violet. JourneyChatPanel.module.scss mirrors these.
export const GARGI_COLORS = {
  tileLight: '#6A45D6',
  tileMid: '#432F9F',
  tileDark: '#270D9F',
  sidePetal: '#E6DAFF',
  tint: '#F3EFFF',
} as const;

export const GARGI_GRADIENT = `linear-gradient(135deg, ${GARGI_COLORS.tileLight} 0%, ${GARGI_COLORS.tileMid} 55%, ${GARGI_COLORS.tileDark} 100%)`;

const { tileLight: TILE_LIGHT, tileMid: TILE_MID, tileDark: TILE_DARK, sidePetal: SIDE_PETAL } = GARGI_COLORS;
const CENTER_PETAL_FILL = '#FFFFFF';

const GargiLotus = (props: SvgIconProps) => {
  // Per-instance ids: several badges render at once (header, every bot reply) and share one DOM.
  const id = useId().replace(/:/g, '');
  const tileId = `gargi-lotus-tile-${id}`;
  const glowId = `gargi-lotus-glow-${id}`;

  return (
    <SvgIcon viewBox="0 0 24 24" {...props}>
      <defs>
        <linearGradient id={tileId} x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={TILE_LIGHT} />
          <stop offset="0.55" stopColor={TILE_MID} />
          <stop offset="1" stopColor={TILE_DARK} />
        </linearGradient>
        <radialGradient id={glowId} cx="12" cy="9.5" r="11" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.16" />
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="24" height="24" rx="5.5" fill={`url(#${tileId})`} />
      <rect width="24" height="24" rx="5.5" fill={`url(#${glowId})`} />
      <g transform="translate(4.25 4.4) scale(0.155)">
        <path d={LEFT_PETAL} fill={SIDE_PETAL} />
        <path d={RIGHT_PETAL} fill={SIDE_PETAL} />
        <path d={CENTER_PETAL} fill={CENTER_PETAL_FILL} />
      </g>
    </SvgIcon>
  );
};

export const GargiLotusMark = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <g transform="translate(1 1.5) scale(0.22)">
      <path d={LEFT_PETAL} opacity={0.6} />
      <path d={RIGHT_PETAL} opacity={0.6} />
      <path d={CENTER_PETAL} />
    </g>
  </SvgIcon>
);

// The monochrome mark as a standard MUI icon (currentColor), for places typed to take one, e.g. a settings tile.
export const GargiLotusIcon = createSvgIcon(
  <g transform="translate(1 1.5) scale(0.22)">
    <path d={LEFT_PETAL} opacity={0.6} />
    <path d={RIGHT_PETAL} opacity={0.6} />
    <path d={CENTER_PETAL} />
  </g>,
  'GargiLotus',
);

export default GargiLotus;
